import React from 'react';
import { AlertCircle, Clock, FileText, ArrowRight, X } from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useDesignSystem, cn } from './designSystem';

const UsageLimitModal = ({ limitType, onClose, currentUser }) => {
  const { typography, componentStyles, colors, effects } = useDesignSystem();
  
  // Determine which limit was reached
  const isWhisperLimit = limitType === 'whisper_time';
  const isNoteLimit = limitType === 'note_generation';
  
  // Calculate usage percentages for progress bars
  const whisperUsagePercent = Math.min(100, (currentUser.whisper_minutes_used / 10) * 100);
  const noteUsagePercent = Math.min(100, (currentUser.notes_generated / 3) * 100);
  
  const handleUpgrade = () => {
    // Redirect to pricing page instead of Stripe
    window.location.href = "https://skribo.no/#pricing";
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <Card className={cn(
        componentStyles.card.glass,
        "w-full max-w-md"
      )}>
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={cn(
                "w-10 h-10 rounded-full",
                colors.status.warning,
                "flex items-center justify-center"
              )}>
                <AlertCircle className="h-6 w-6 text-amber-600 dark:text-amber-400" />
              </div>
              <CardTitle className="text-xl">
                Demo-konto begrensning nådd
              </CardTitle>
            </div>
            <Button 
              variant="ghost" 
              className="h-8 w-8 p-0 rounded-full"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          <p className={cn(
            typography.paragraph,
            colors.neutral.textSecondary
          )}>
            {isWhisperLimit ? 
              'Du har nådd grensen på 10 minutter med transkripsjon for demo-kontoen din.' : 
              'Du har generert alle 3 tilgjengelige notater for demo-kontoen din.'}
          </p>
          
          <div className="space-y-3">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  Transkripsjon
                </span>
                <span>
                  {currentUser.whisper_minutes_used.toFixed(1)}/10 minutter
                </span>
              </div>
              <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700">
                <div 
                  className={cn(
                    "h-full rounded-full",
                    whisperUsagePercent >= 100 ? colors.status.error : colors.primary.solid
                  )}
                  style={{ width: `${whisperUsagePercent}%` }}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="flex items-center gap-1">
                  <FileText className="h-4 w-4" />
                  Notater generert
                </span>
                <span>{currentUser.notes_generated}/3 notater</span>
              </div>
              <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700">
                <div 
                  className={cn(
                    "h-full rounded-full",
                    noteUsagePercent >= 100 ? colors.status.error : colors.primary.solid
                  )}
                  style={{ width: `${noteUsagePercent}%` }}
                />
              </div>
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
            <h4 className="font-medium mb-2">Oppgrader for å få full tilgang:</h4>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <ArrowRight className="h-4 w-4 mt-0.5 text-green-500" />
                <span>Ubegrenset transkripsjonstid</span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowRight className="h-4 w-4 mt-0.5 text-green-500" />
                <span>Ubegrenset notatgenerering</span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowRight className="h-4 w-4 mt-0.5 text-green-500" />
                <span>Tilgang til alle maler og ressurser</span>
              </li>
            </ul>
          </div>
        </CardContent>
        
        <CardFooter className="flex justify-between gap-4">
          <Button 
            variant="outline" 
            onClick={onClose}
            className={componentStyles.button.secondary}
          >
            Lukk
          </Button>
          <Button 
            className={cn(
              componentStyles.button.primary,
              "flex-1"
            )} 
            onClick={handleUpgrade}
          >
            Oppgrader nå
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default UsageLimitModal;